import { Constants, Contact } from '@salesforce/scv-connector-base';
import {
  ERROR_CODE,
  LOG_LEVEL,
  NOTIFICATION_TYPE,
  getConfig,
  sendNotification,
} from '@amc-technology/davinci-api';

import { DeferredPromiseKind } from '../Model/Classes/DeferredPromise';
import { Injectable } from '@angular/core';
import { LoggerService } from './logger.service';
import { VoiceCallRecordCreationEnum } from '../Model/Enums/VoiceCallRecordCreationEnum';

/**
 * This service is used to handle the configuration from Creators Studio
 *
 * @export
 * @class ConfigService
 */

@Injectable({
  providedIn: 'root',
})
export class StudioConfigService {
  log: (
    logLevel: LOG_LEVEL,
    fName: string,
    message: string,
    object?: any,
    errorCode?: ERROR_CODE,
    localTime?: Date
  ) => void;

  private readonly root: ByotIAppConfigurationVariables = {
    PhoneNumberFormat: new Map([]),
    ClickToDialPhoneReformatMap: new Map([]),
    LocalStorageCheckTimeout: 2000,
    LocalLog: false
  };
  phoneNumberFormatMap?: Map<string, string>;
  clickToDialPhoneReformatMap?: any;
  localStorageCheckTimeout?: number;
  localLog?: boolean;

  private readonly callControls: ByotIAppConfigurationVariables = {
    eventTimeout: 5000,
    rejectEnabled: true,
    addCallerEnabled: true,
    warmTransferEnabled: false,
    conferenceEnabled: true,
    holdEnabled: true,
    swapEnabled: true,
    muteEnabled: true,
    removeParticipantVariant: Constants.REMOVE_PARTICIPANT_VARIANT.ALWAYS,
    queueWaitTime: true,
    keypadEnabled: true,
    outboundAutoConnect: true,
    conferenceDropParty: true,
    blindTransferEnabled: true,
    defaultUnkownDirection: Constants.CALL_TYPE.INBOUND
  };

  callControlsDefaults: {
    readonly eventTimeout: number;
  } = {
      eventTimeout: this.callControls.eventTimeout as number,
    };

  eventTimeout?: number;
  rejectEnabled?: boolean;
  addCallerEnabled?: boolean;
  warmTransferEnabled?: boolean;
  conferenceEnabled?: boolean;
  holdEnabled?: boolean;
  swapEnabled?: boolean;
  muteEnabled?: boolean;
  removeParticipantVariant?: Constants.REMOVE_PARTICIPANT_VARIANT;
  keypadEnabled?: boolean;
  outboundAutoConnect?: boolean;
  conferenceDropParty?: boolean;
  blindTransferEnabled?: boolean;
  defaultUnkownDirection?: Constants.CALL_TYPE;

  private readonly presenceMapping: ByotIAppConfigurationVariables = {
    presenceChangeTimeout: 5000,
    channelToSalesforce: new Map<string, string>([]),
    salesforceToChannel: new Map<string, string>([]),
  };

  presenceMappingDefaults: {
    readonly presenceChangeTimeout: number;
  } = {
      presenceChangeTimeout: this.presenceMapping.presenceChangeTimeout as number,
    };

  presenceChangeTimeout?: number;
  channelToSalesforceMap: Map<string, string> = new Map<string, string>([]);
  salesforceToChannelMap?: Record<string, string>;

  private readonly transcription: ByotIAppConfigurationVariables = {
    VoiceCallRecordCreation: VoiceCallRecordCreationEnum.ClientSideCallCreation,
    VoiceCallRecordToCAD: new Map([]),
    VendorCallKeyCAD: '',
    CommunicationServiceUrl: '',
    AuthToken: '',
    RequestTimeout: 5000,
    CloseCallOnUpdate: false
  };
  VoiceCallRecordCreation?: VoiceCallRecordCreationEnum;
  VoiceCallRecordToCAD?: Map<string, string>;
  VendorCallKeyCAD?: string;
  communicationServiceUrl?: string;
  authToken?: string;
  requestTimeout?: number;
  closeCallOnUpdate?: boolean;

  private readonly queues: ByotIAppConfigurationVariables = {
    ctiIdToSalesforceId: new Map([]),
    queueWaitTime: true,
  };
  ctiIdToSalesforceIdMap?: Map<string, string>;
  queueWaitTime?: boolean;

  private readonly contacts: ByotIAppConfiguration = {
    variables: {
      TypeSortingOrder: 'agent,queue,phonenumber,phonebook,flow',
      EnableContextualContacts: true,
      EnableAgents: true,
      EnableQueues: true,
      EnablePhoneNumbers: true,
      EnablePhoneBooks: true,
      EnableFlows: true
    } as ByotIAppConfigurationVariables,
    'Queue 1': {
      variables: {
        id: 'id1',
        type: 'Queue',
        phoneNumber: undefined,
        prefix: undefined,
        extension: undefined,
        endpointARN: undefined,
        queue: 'id1',
        availability: undefined,
        recordId: undefined,
        description: undefined,
        queueWaitTime: undefined,
      } as ByotIAppConfigurationVariables,
    } as ByotIAppConfiguration,
    'Queue 2': {
      variables: {
        id: 'id2',
        type: 'Queue',
        phoneNumber: undefined,
        prefix: undefined,
        extension: undefined,
        endpointARN: undefined,
        queue: 'id2',
        availability: undefined,
        recordId: undefined,
        description: undefined,
        queueWaitTime: undefined,
      } as ByotIAppConfigurationVariables,
    } as ByotIAppConfiguration,
  };
  enableContextualContacts!: boolean;
  typeSortingOrder!: Constants.CONTACT_TYPE[];
  enableAgents!: boolean;
  enableQueues!: boolean;
  enablePhoneNumbers!: boolean;
  enablePhoneBooks!: boolean;
  enableFlows!: boolean;
  contactList: Contact[] = [];

  private readonly loginConfig: ByotIAppConfigurationVariables = {
    MaxAttempts: 10,
    AttemptFrequencyMs: 10000
  };

  maxLoginAttempts?: number;
  loginAttemptFrequencyMs?: number;

  private readonly eventTimeouts = {
    MergePeriod: 5000,
    AgentStatus: 5000,
    BlindTransfer: 5000,
    AcceptDecline: 5000,
    Conference: 5000,
    Hangup: 5000,
    HoldUnhold: 5000,
    Logout: 5000,
    Mute: 5000,
    OutboundDial: 5000,
    AddParticiant: 5000,
    AgentStatusEcho: 1000
  };

  MergePeriod?: number;
  AgentStatus?: number;
  BlindTransfer?: number;
  AcceptDecline?: number;
  Conference?: number;
  Hangup?: number;
  HoldUnhold?: number;
  Logout?: number;
  Mute?: number;
  OutboundDial?: number;
  AddParticiant?: number;
  AgentStatusEcho?: number;

  promiseTimeouts: Record<DeferredPromiseKind, number> = {
    AgentStatus: 5000,
    BlindTransfer: 5000,
    AcceptReject: 5000,
    Conference: 5000,
    Hangup: 5000,
    Hold: 5000,
    Logout: 5000,
    Mute: 5000,
    OutboundDial: 20000,
    Participant: 5000,
    PendingMerge: 500,
    AgentStatusEcho: 1000
  };


  /**
   * This is the default configuration for the app. Any configurations in Creators Studio will override these values if they are valid.
   * @type {ByotIAppConfiguration}
   * @memberof StudioConfigService
   */
  private readonly studioConfig: ByotIAppConfiguration = {
    variables: this.root,
    CallControls: this.callControls,
    PresenceMapping: this.presenceMapping,
    Transcription: this.transcription,
    Queues: this.queues,
    Contacts: this.contacts,
    Login: this.loginConfig,
    EventTimeouts: this.eventTimeouts
  };

  constructor(private readonly loggerService: LoggerService) {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.log = this.loggerService.log;
  }

  async getStudioConfig(): Promise<any> {
    console.log('getStudioConfig');
    const functionName = 'SCS.' + 'getStudioConfig';
    try {
      this.log(LOG_LEVEL.Information, functionName, 'Getting Studio Config');
      const config = await getConfig();
      this.log(LOG_LEVEL.Debug, functionName, 'Studio Config', config);
      this.setConfigs(config);
      this.log(LOG_LEVEL.Information, functionName, 'Getting Studio Config');
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Error getting Studio Config', error);
    }
  }

  private setConfigs(config: ByotIAppConfiguration): void {
    const functionName = 'SCS.' + 'setConfigs';
    try {
      this.log(LOG_LEVEL.Debug, functionName, 'Updating Client Configs with values from Studio');
      this.setRootConfigs(config.variables);
      this.setCallControlConfigs(
        config.CallControls.variables as ByotIAppConfigurationVariables
      );
      this.setpresenceMappings(
        config.PresenceMapping.variables as ByotIAppConfigurationVariables
      );
      this.setTranscriptionConfigs(
        config.Transcription.variables as ByotIAppConfigurationVariables
      );
      this.setQueueConfigs(
        config.Queues.variables as ByotIAppConfigurationVariables
      );
      this.setContactConfigs(config.Contacts as ByotIAppConfiguration);
      this.setLoginConfigs(config.Login.variables as ByotIAppConfigurationVariables);
      this.setEventTimeoutConfigs(config?.['Event Timeouts']?.variables as ByotIAppConfigurationVariables);
      this.updateStudioConfig();
      this.log(LOG_LEVEL.Debug, functionName, 'Updated Client Config', this.studioConfig);
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Error setting Studio Config', error);
    }
  }

  private setRootConfigs(config?: ByotIAppConfigurationVariables): void {
    const functionName = 'SCS.' + 'setRootConfigs';
    try {
      if (!config) {
        throw new Error('Missing Root Configs');
      }
      this.log(LOG_LEVEL.Debug, functionName, 'Checking Root Configs');
      this.phoneNumberFormatMap = this.updateConfigs('PhoneNumberFormat', this.root.PhoneNumberFormat, config.PhoneNumberFormat);
      this.clickToDialPhoneReformatMap = this.updateConfigs('ClickToDialPhoneReformatMap', this.root.ClickToDialPhoneReformatMap, config.ClickToDialPhoneReformatMap);
      this.localStorageCheckTimeout = this.updateConfigs('LocalStorageCheckTimeout', this.root.LocalStorageCheckTimeout, config.LocalStorageCheckTimeout);
      this.localLog = this.updateConfigs('LocalLog', this.root.LocalLog, config.LocalLog);
      this.log(LOG_LEVEL.Trace, functionName, 'Root Config', this.root);
      if (this.localLog === true) {
        localStorage.setItem('LocalLog', this.localLog?.toString() ?? 'false');
      }
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Error setting Root Config', error);
    }
  }

  private setCallControlConfigs(config?: ByotIAppConfigurationVariables): void {
    const functionName = 'SCS.' + 'setCallControlConfigs';
    try {
      if (!config) {
        throw new Error('Missing CallControls Configs');
      }
      this.log(LOG_LEVEL.Debug, functionName, 'Checking Call Control Configs');
      this.eventTimeout = this.updateConfigs('eventTimeout', this.callControls.eventTimeout, config.eventTimeout);
      this.rejectEnabled = this.updateConfigs('rejectEnabled', this.callControls.rejectEnabled, config.rejectEnabled);
      this.addCallerEnabled = this.updateConfigs('addCallerEnabled', this.callControls.addCallerEnabled, config.addCallerEnabled);
      this.conferenceEnabled = this.updateConfigs('conferenceEnabled', this.callControls.conferenceEnabled, config.conferenceEnabled);
      this.holdEnabled = this.updateConfigs('holdEnabled', this.callControls.holdEnabled, config.holdEnabled);
      this.swapEnabled = this.updateConfigs('swapEnabled', this.callControls.swapEnabled, config.swapEnabled);
      this.muteEnabled = this.updateConfigs('muteEnabled', this.callControls.muteEnabled, config.muteEnabled);
      this.removeParticipantVariant = this.updateConfigs('removeParticipantVariant', this.callControls.removeParticipantVariant, config.removeParticipantVariant);
      this.keypadEnabled = this.updateConfigs('keypadEnabled', this.callControls.keypadEnabled, config.keypadEnabled);
      this.outboundAutoConnect = this.updateConfigs('Outbound Auto Connect', this.callControls.outboundAutoConnect, config?.['Outbound Auto Connect']);
      this.conferenceDropParty = this.updateConfigs('conferenceDropParty', this.callControls.conferenceDropParty, config?.['conferenceDropParty']);
      this.blindTransferEnabled = this.updateConfigs('blindTransferEnabled', this.callControls.blindTransferEnabled, config?.['blindTransferEnabled']);
      this.warmTransferEnabled = this.updateConfigs('warmTransferEnabled', this.callControls.warmTransferEnabled, config?.['warmTransferEnabled']);
      this.defaultUnkownDirection = this.updatedefaultUnkownDirectionConfig('defaultUnkownDirection', this.callControls.defaultUnkownDirection, config?.['defaultUnkownDirection']);
      this.log(LOG_LEVEL.Trace, functionName, 'Call Control Config', this.callControls);
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Error setting Call Control Config', error);
    }
  }

  private setpresenceMappings(config?: ByotIAppConfigurationVariables): void {
    const functionName = 'SCS.' + 'setpresenceMappings';
    try {
      if (!config) {
        throw new Error('Missing PresenceMapping Configs');
      }
      this.log(LOG_LEVEL.Debug, functionName, 'Checking Presence Mapping Configs');
      this.presenceChangeTimeout = this.updateConfigs('presenceChangeTimeout', this.presenceMapping.presenceChangeTimeout, config.presenceChangeTimeout);
      this.channelToSalesforceMap = new Map(Object.entries(this.updateConfigs('channelToSalesforce', this.presenceMapping.channelToSalesforce, config.channelToSalesforce)));
      this.salesforceToChannelMap = this.updateConfigs('salesforceToChannel', this.presenceMapping.salesforceToChannel, config.salesforceToChannel);
      this.log(LOG_LEVEL.Trace, functionName, 'Presence Mapping Config', this.presenceMapping);
    } catch (error) {
      this.log( LOG_LEVEL.Error, functionName, 'Error setting Presence Mapping Config', error);
    }
  }

  private setTranscriptionConfigs(
    config?: ByotIAppConfigurationVariables
  ): void {
    const functionName = 'SCS.' + 'setTranscriptionConfigs';
    try {
      if (!config) {
        throw new Error('Missing Transcription Configs');
      }
      this.log(LOG_LEVEL.Debug, functionName, 'Checking Transcription Configs');
      this.VoiceCallRecordCreation = this.updateVoiceCallRecordCreationConfig('VoiceCallRecordCreation', this.transcription.VoiceCallRecordCreation, config.VoiceCallRecordCreation);
      this.VoiceCallRecordToCAD = this.updateConfigs('VoiceCallRecordToCAD', this.transcription.VoiceCallRecordToCAD, config.VoiceCallRecordToCAD);
      this.VendorCallKeyCAD = this.updateConfigs('VendorCallKeyCAD', this.transcription.VendorCallKeyCAD, config.VendorCallKeyCAD, true);
      this.communicationServiceUrl = this.updateConfigs('CommunicationServiceUrl', this.transcription.CommunicationServiceUrl, config.CommunicationServiceUrl, true);
      this.authToken = this.updateConfigs('AuthToken', this.transcription.AuthToken, config.AuthToken, true, true);
      this.requestTimeout = this.updateConfigs('RequestTimeout', this.transcription.RequestTimeout, config.RequestTimeout);
      this.closeCallOnUpdate = this.updateConfigs('Close call on Update', this.transcription.CloseCallOnUpdate, config?.['Close call on Update']);
      this.log(LOG_LEVEL.Trace, functionName, 'Transcription Config', this.transcription);
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Error setting Transcription Config', error);
    }
  }

  private setQueueConfigs(config?: ByotIAppConfigurationVariables): void {
    const functionName = 'SCS.' + 'setQueueConfigs';
    try {
      if (!config) {
        throw new Error('Missing Queue Configs');
      }
      this.log(LOG_LEVEL.Debug, functionName, 'Checking Queue Configs');
      this.ctiIdToSalesforceIdMap = this.updateConfigs('ctiIdToSalesforceId', this.queues.ctiIdToSalesforceId, config.ctiIdToSalesforceId);
      this.queueWaitTime = this.updateConfigs('SalesforceQueueWaitTime', this.queues.queueWaitTime, config.SalesforceQueueWaitTime);
      this.log(LOG_LEVEL.Trace, functionName, 'Queue Config', this.queues);
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Error setting Queue Config', error);
    }
  }

  private setContactConfigs(config?: ByotIAppConfiguration): void {
    const functionName = 'SCS.' + 'setContactConfigs';
    try {
      if (!config) {
        throw new Error('Missing Contact Configs');
      }
      let contactList = config;
      this.log(LOG_LEVEL.Debug, functionName, 'Checking Contact Configs');
      if (Object.keys(config).length < 2) {
        this.log(LOG_LEVEL.Warning, functionName, 'No Contacts Configured, using default', this.contacts);
        contactList = this.contacts;
      }
      for (const key in contactList) {
        if (contactList.hasOwnProperty(key) && key !== 'variables') {
          const display = (contactList[key].variables as ByotIAppConfigurationVariables)['Display Contact'];
          if (display === false) {
            // disables a contact from displaying without having to remove the config
            continue;
          }
          const rawType = (contactList[key].variables as ByotIAppConfigurationVariables)['type'];
          let type: Constants.CONTACT_TYPE | undefined;
          switch (rawType) {
          case Constants.CONTACT_TYPE.PHONEBOOK:
          case Constants.CONTACT_TYPE.QUEUE:
          case Constants.CONTACT_TYPE.PHONENUMBER:
          case Constants.CONTACT_TYPE.AGENT:
          case Constants.CONTACT_TYPE.FLOW:
            type = rawType;
          }

          const contact: Contact = new Contact({
            name: key,
            id: (contactList[key].variables as ByotIAppConfigurationVariables)[
              'id'
            ]
              ? (
                  contactList[key].variables as ByotIAppConfigurationVariables
              )['id']?.toString()
              : undefined,
            type,
            phoneNumber:
              (contactList[key].variables as ByotIAppConfigurationVariables)[
                'phoneNumber'
              ]?.toString() || undefined,
            prefix:
              (contactList[key].variables as ByotIAppConfigurationVariables)[
                'prefix'
              ]?.toString() || undefined,
            extension:
              (contactList[key].variables as ByotIAppConfigurationVariables)[
                'extension'
              ]?.toString() || undefined,
            endpointARN:
              (contactList[key].variables as ByotIAppConfigurationVariables)[
                'endpointARN'
              ]?.toString() || undefined,
            queue:
              (contactList[key].variables as ByotIAppConfigurationVariables)[
                'queue'
              ]?.toString() || undefined,
            availability:
              (contactList[key].variables as ByotIAppConfigurationVariables)[
                'availability'
              ]?.toString() || undefined,
            recordId:
              (contactList[key].variables as ByotIAppConfigurationVariables)[
                'recordId'
              ]?.toString() || undefined,
            description:
              (contactList[key].variables as ByotIAppConfigurationVariables)[
                'description'
              ]?.toString() || undefined,
            queueWaitTime:
              (contactList[key].variables as ByotIAppConfigurationVariables)[
                'queueWaitTime'
              ]?.toString() || undefined,
          });
          this.log(LOG_LEVEL.Loop, functionName, 'Adding contact to list', contact);
          this.contactList.push(contact);
        }
      }

      const contactsConfigs: ByotIAppConfigurationVariables = config.variables;
      this.enableContextualContacts = this.updateConfigs('EnableContextualContacts', this.contacts.variables.EnableContextualContacts, contactsConfigs?.['Display Contextual Contacts']);

      const rawTypeSortingOrder = this.updateConfigs('TypeSortingOrder', this.contacts.variables.TypeSortingOrder, contactsConfigs?.['Type Sorting Order']).toLowerCase();
      const allowedTypes = ['agent', 'queue', 'phonenumber', 'phonebook', 'flow'];
      this.typeSortingOrder = this.enforceCommaSeparatedList(rawTypeSortingOrder, allowedTypes);

      this.enableAgents = this.updateConfigs('DisplayAgents', this.contacts.variables.EnableAgents, contactsConfigs?.['Display Agents']);
      this.enableQueues = this.updateConfigs('DisplayQueues', this.contacts.variables.EnableQueues, contactsConfigs?.['Display Queues']);
      this.enablePhoneNumbers = this.updateConfigs('DisplayPhoneNumbers', this.contacts.variables.EnablePhoneNumbers, contactsConfigs?.['Display Phone Numbers']);
      this.enablePhoneBooks = this.updateConfigs('DisplayPhoneBooks', this.contacts.variables.EnablePhoneBooks, contactsConfigs?.['Display Phone Books']);
      this.enableFlows = this.updateConfigs('DisplayFlows', this.contacts.variables.EnableFlows, contactsConfigs?.['Display Flows']);

      this.log(LOG_LEVEL.Trace, functionName, 'Contact Config', this.contactList);
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Error setting Contact Config', error);
    }
  }

  private setLoginConfigs(config?: ByotIAppConfigurationVariables): void {
    const functionName = 'SCS.' + 'setLoginConfigs';
    try {
      if (!config) {
        this.log(LOG_LEVEL.Error, functionName, 'Error setting Login Config');
      }

      this.maxLoginAttempts = this.updateConfigs('MaxAttempts', this.loginConfig.MaxAttempts, config?.MaxAttempts);
      this.loginAttemptFrequencyMs = this.updateConfigs('AttemptFrequencyMs', this.loginConfig.AttemptFrequencyMs, config?.AttemptFrequencyMs);

      this.log(LOG_LEVEL.Trace, functionName, 'Login Config', this.loginConfig);
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Error setting Login Config', error);
    }
  }

  private setEventTimeoutConfigs(config?: ByotIAppConfigurationVariables): void {
    const functionName = 'SCS.' + 'setEventTimeoutConfigs';
    try {
      if (!config) {
        this.log(LOG_LEVEL.Error, functionName, 'Error setting Event Timeout Config');
      }

      this.MergePeriod = this.updateConfigs('Merge Period', this.eventTimeouts.MergePeriod, config?.['Merge Period']);
      this.AgentStatus = this.updateConfigs('Agent Status', this.eventTimeouts.AgentStatus, config?.['Agent Status']);
      this.BlindTransfer = this.updateConfigs('Blind Transfer', this.eventTimeouts.BlindTransfer, config?.['Blind Transfer']);
      this.AcceptDecline = this.updateConfigs('Accept / Decline', this.eventTimeouts.AcceptDecline, config?.['Accept / Decline']);
      this.Conference = this.updateConfigs('Conference', this.eventTimeouts.Conference, config?.Conference);
      this.Hangup = this.updateConfigs('Hangup', this.eventTimeouts.Hangup, config?.Hangup);
      this.HoldUnhold = this.updateConfigs('Hold / Unhold', this.eventTimeouts.HoldUnhold, config?.['Hold / Unhold']);
      this.Logout = this.updateConfigs('Logout', this.eventTimeouts.Logout, config?.Logout);
      this.Mute = this.updateConfigs('Mute', this.eventTimeouts.Mute, config?.Mute);
      this.OutboundDial = this.updateConfigs('Outbound Dial', this.eventTimeouts.OutboundDial, config?.['Outbound Dial']);
      this.AddParticiant = this.updateConfigs('Add Participant', this.eventTimeouts.AddParticiant, config?.['Add Participant']);
      this.AgentStatusEcho = this.updateConfigs('Agent Status Echo', this.eventTimeouts.AgentStatusEcho, config?.['Agent Status Echo']);
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Error setting Event Timeout Config', error);
    } finally {
      this.promiseTimeouts.AgentStatus = this.AgentStatus ?? this.eventTimeouts.AgentStatus;
      this.promiseTimeouts.BlindTransfer = this.BlindTransfer ?? this.eventTimeouts.BlindTransfer;
      this.promiseTimeouts.AcceptReject = this.AcceptDecline ?? this.eventTimeouts.AcceptDecline;
      this.promiseTimeouts.Conference = this.Conference ?? this.eventTimeouts.Conference;
      this.promiseTimeouts.Hangup = this.Hangup ?? this.eventTimeouts.Hangup;
      this.promiseTimeouts.Hold = this.HoldUnhold ?? this.eventTimeouts.HoldUnhold;
      this.promiseTimeouts.Logout = this.Logout ?? this.eventTimeouts.Logout;
      this.promiseTimeouts.Mute = this.Mute ?? this.eventTimeouts.Mute;
      this.promiseTimeouts.OutboundDial = this.OutboundDial ?? this.eventTimeouts.OutboundDial;
      this.promiseTimeouts.Participant = this.AddParticiant ?? this.eventTimeouts.AddParticiant;
      this.promiseTimeouts.PendingMerge = this.MergePeriod ?? this.eventTimeouts.MergePeriod;
      this.promiseTimeouts.AgentStatusEcho = this.AgentStatusEcho ?? this.eventTimeouts.AgentStatusEcho;

      this.log(LOG_LEVEL.Trace, functionName, 'Event Timeout Config', this.promiseTimeouts);
    }
  }

  private updateStudioConfig(): void {
    const functionName = 'SCS.' + 'updateStudioConfig';
    try {
      this.log(LOG_LEVEL.Debug, functionName, 'Updating Studio Config');
      this.studioConfig.variables = this.root;
      this.studioConfig.CallControls = this.callControls;
      this.studioConfig.PresenceMapping = this.presenceMapping;
      this.studioConfig.Transcription = this.transcription;
      this.studioConfig.Queues = this.queues;
      this.studioConfig.Login = this.loginConfig;
      this.studioConfig.EventTimeouts = this.eventTimeouts;
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Error updating Studio Config', error);
    }
  }

  /**
   * this function will compare the values of the default config and the studio config and return the correct value
   *
   * @private
   * @param {*} defaultConfig
   * @param {*} studioConfig
   * @return {*}  {void}
   * @memberof StudioConfigService
   */
  private updateConfigs(
    configName: string,
    defaultConfig: any,
    studioConfig: any,
    critical: boolean = false,
    pii: boolean = false
  ): any {
    const functionName = 'SCS.' + 'updateConfigs';
    try {
      this.log(LOG_LEVEL.Loop, functionName, `Comparing Configs for : ${configName}`);
      if (
        studioConfig != null &&
        typeof defaultConfig === typeof studioConfig
      ) {
        this.log( LOG_LEVEL.Loop, functionName, 'Valid Studio Config', pii ? { configName } : { configName, studioConfig });
        return studioConfig;
      } else {
        this.log(critical ? LOG_LEVEL.Critical : LOG_LEVEL.Error, functionName, 'Invalid Studio Config, using Default Config', pii ? { configName, defaultConfig } : { configName, defaultConfig, studioConfig });
        if (critical) {
          sendNotification('Configuration Error. Please contact your administrator for assistance.', NOTIFICATION_TYPE.Error);
        }
        return defaultConfig;
      }
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Error comparing Configs', error);
    }
  }

  private updateVoiceCallRecordCreationConfig(
    configName: string,
    defaultConfig: any,
    studioConfig: any
  ): VoiceCallRecordCreationEnum {
    const functionName = 'SCS.' + 'updateVoiceCallRecordCreationConfig';

    try {
      this.log(LOG_LEVEL.Loop, functionName, `Comparing Configs for : ${configName}`);

      //  Return the value of the studioConfig to the VoiceCallRecordCreationConfig enum type or if the value specified does not match one of the enum types, return the default value.
      if (
        studioConfig != null &&
        typeof defaultConfig ===
          typeof VoiceCallRecordCreationEnum[studioConfig]
      ) {
        this.log(LOG_LEVEL.Loop, functionName, 'Valid Studio Config', {
          configName,
          studioConfig,
        });
        return VoiceCallRecordCreationEnum[
          studioConfig as keyof typeof VoiceCallRecordCreationEnum
        ];
      } else {
        this.log(LOG_LEVEL.Error, functionName, 'Invalid Studio Config, using Default Config', { configName, defaultConfig, studioConfig });
        return VoiceCallRecordCreationEnum[
          defaultConfig as keyof typeof VoiceCallRecordCreationEnum
        ];
      }
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Error comparing Configs', error);
      throw error;
    }
  }

  private updatedefaultUnkownDirectionConfig(
    configName: string,
    defaultConfig: any,
    studioConfig: any
  ): Constants.CALL_TYPE {
    const functionName = 'SCS.' + 'updatedefaultUnkownDirectionConfig';
    try {
      this.log(LOG_LEVEL.Loop, functionName, `Comparing Configs for : ${configName}`);
      if (
        studioConfig != null &&
        typeof defaultConfig === typeof Constants.CALL_TYPE[studioConfig]
      ) {
        this.log(LOG_LEVEL.Loop, functionName, 'Valid Studio Config', {
          configName,
          studioConfig,
        });
        return Constants.CALL_TYPE[
          studioConfig as keyof typeof Constants.CALL_TYPE
        ];
      } else {
        this.log(LOG_LEVEL.Error, functionName, 'Invalid Studio Config, using Default Config', { configName, defaultConfig, studioConfig });
        return Constants.CALL_TYPE[
          defaultConfig as keyof typeof Constants.CALL_TYPE
        ];
      }
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Error comparing Configs', error);
      // Fallback to defaultConfig if error occurs
      return Constants.CALL_TYPE[
        defaultConfig as keyof typeof Constants.CALL_TYPE
      ];
    }
  }

  private enforceCommaSeparatedList(value: string, allowed: string[]): Constants.CONTACT_TYPE[] {
    const fname = 'SCS.enforceCommaSeparatedList';
    try {
      if (typeof value !== 'string') {
        throw new Error('Input value is not a string.');
      }
      const items = value.split(',')
        .map(s => s.trim().toLowerCase())
        .filter(Boolean)
        .map(item => {
        // Find the matching enum key (case-insensitive)
          const match = allowed.find(a => a.toLowerCase() === item);
          return match ? Constants.CONTACT_TYPE[match.toUpperCase() as keyof typeof Constants.CONTACT_TYPE] : undefined;
        })
        .filter((v): v is Constants.CONTACT_TYPE => v !== undefined);

      if (items.length === 0) {
        this.log(LOG_LEVEL.Error, fname, 'No valid TypeSortingOrder values', { value });
        // fallback to all allowed types
        return allowed.map(a => Constants.CONTACT_TYPE[a.toUpperCase() as keyof typeof Constants.CONTACT_TYPE]);
      }
      return items;
    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Failed to validate list.', error);
      return [
        Constants.CONTACT_TYPE.QUEUE,
        Constants.CONTACT_TYPE.AGENT,
        Constants.CONTACT_TYPE.PHONENUMBER,
        Constants.CONTACT_TYPE.PHONEBOOK,
        Constants.CONTACT_TYPE.FLOW
      ];
    }
  }
}

// TODO: These are based on the IAppConfiguration from the DaVinci API. We should update the DaVinci API to include additional types for the variables.
export interface ByotIAppConfiguration {
  variables: ByotIAppConfigurationVariables;
  [child: string]: ByotIAppConfiguration | ByotIAppConfigurationVariables;
}

export type ByotIAppConfigurationVariables = Record<string, | boolean
    | number
    | string
    | string[]
    | Map<string, string>
    | VoiceCallRecordCreationEnum
    | undefined>;
